// The last few posts, and what happened to each.
//
// A summary, not a second Posts screen: five rows, each linking to the one
// place where a post can actually be worked on. The per-destination detail —
// which network took it, which failed, the live link — stays there, because a
// dashboard that grows a delivery table is a dashboard nobody can read at a
// glance.
//
// "Performance" here means our delivery record: how many of the post's
// destinations accepted it. It is not a view count, and there is no view count
// to show — see the note at the foot of the dashboard.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listPosts } from "../lib/api";
import { STATUS_CHIP, STATUS_LABEL } from "../lib/postStatus";
import { PLATFORM_ICON } from "../components/PlatformIcons";
import { Loading } from "../components/Spinner";
import { logError } from "../lib/logger";
import { formatInZone } from "../lib/timezone";
import { useZone } from "../lib/userSettings";
import { PLATFORM_LABEL, type Platform, type Post } from "../lib/types";

const SHOWN = 5;

/** One mark per network this post went to, de-duplicated. */
function platformsOf(p: Post): Platform[] {
  const seen = new Set<Platform>();
  for (const d of p.deliveries ?? []) seen.add(d.platform);
  return [...seen];
}

const said = (iso: string, zone: string) =>
  formatInZone(iso, zone, {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });

export function RecentPostsCard({ workspaceId }: { workspaceId: string | null }) {
  const zone = useZone();
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setPosts((await listPosts(workspaceId)).slice(0, SHOWN));
      setError(null);
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "dashboard_recent_posts", workspaceId });
    }
  }, [workspaceId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="card dash-card recent-card" aria-labelledby="recent-head">
      <div className="card-head">
        <h2 id="recent-head">Recent posts</h2>
        <Link to="/posts" className="card-head-link">View all →</Link>
      </div>

      {error ? (
        <p className="err empty-state">
          Your recent posts could not be loaded. <Link to="/posts">Open the Posts screen</Link> —
          they are still there.
        </p>
      ) : !posts ? (
        <Loading label="Reading your recent posts…" />
      ) : posts.length === 0 ? (
        <p className="muted empty-state">
          Nothing published yet. <Link to="/compose">Write your first post</Link> and it appears
          here with what each network did with it.
        </p>
      ) : (
        <ul className="recent-list">
          {posts.map((p) => {
            const delivered = p.deliveries?.filter((d) => d.status === "published").length ?? 0;
            const total = p.deliveries?.length ?? 0;
            const label = p.title || p.caption?.trim() || `Untitled ${p.kind}`;
            return (
              <li key={p.id}>
                <Link to={`/posts/${p.id}`} className="recent-what">{label}</Link>

                <span className="platform-badges">
                  {platformsOf(p).map((platform) => {
                    const Icon = PLATFORM_ICON[platform];
                    return (
                      <span key={platform} title={PLATFORM_LABEL[platform]}>
                        <Icon className="platform-icon" />
                        <span className="visually-hidden">{PLATFORM_LABEL[platform]}</span>
                      </span>
                    );
                  })}
                </span>

                <span className="muted recent-when">
                  {p.scheduled_at && p.status === "scheduled"
                    ? said(p.scheduled_at, zone)
                    : said(p.created_at, zone)}
                </span>

                <span className="muted recent-reach">
                  {total > 0 ? `${delivered} of ${total} delivered` : "no destinations"}
                </span>

                <span className={`chip ${STATUS_CHIP[p.status]}`}>{STATUS_LABEL[p.status]}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
